import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import ContactValidation from 'donor-elf-web/validations/contact';
import ContactModel from 'donor-elf-web/models/contact';
import AbilitiesService from 'donor-elf-web/services/abilities';
import NewsletterContactModel from 'donor-elf-web/models/newsletter-contact';
import Settings from 'donor-elf-web/services/settings';
import Store from '@ember-data/store';

interface ComponentArgs {
  contact: ContactModel;
  newsletterContact?: NewsletterContactModel;
  onNewsletterContactCreated: (
    newsletterContact: NewsletterContactModel,
  ) => void;
  onNewsletterContactDestroyed: (
    newsletterContact: NewsletterContactModel,
  ) => void;
}

export default class ContactDetailsAddressComponent extends Component<ComponentArgs> {
  @service declare abilities: AbilitiesService;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked saving = false;
  @tracked editing = false;
  @tracked showSubscribedToNewsletterButIncompleteAddressError = false;
  @tracked changeset?: any;
  @tracked showValidationErrors = false;
  @tracked error?: any;

  get mapLink() {
    if (isEmpty(this.args.contact?.city)) {
      return null;
    }
    let { address1, city, state, zip, country } = this.args.contact;
    let mapQuery = `${address1} ${city}, ${state} ${zip} ${country}`
      .replace('null', '')
      .replace(' ', '+');
    return `https://maps.google.com/maps?q=${mapQuery}`;
  }

  get showEditIcon() {
    return this.abilities.hasAbility('edit_contact') && !this.editing;
  }

  get subscribedToNewsletter() {
    return this.args.newsletterContact != null;
  }

  get showSubscribeToNewsletterCheckbox() {
    return this.settings.fundIsSyncedWithUserNewsletterAccount;
  }

  @action
  reset() {
    this.saving = false;
    this.editing = false;
    this.showSubscribedToNewsletterButIncompleteAddressError = false;
    this.error = undefined;
    this.changeset = undefined;
    this.showValidationErrors = false;
  }

  @action
  setEditing(val: boolean) {
    this.editing = val;
    if (this.editing) {
      this.changeset = new Changeset(
        this.args.contact,
        lookupValidator(ContactValidation),
        ContactValidation,
      );
      if (this.showSubscribeToNewsletterCheckbox) {
        this.changeset.set(
          'subscribeToNewsletter',
          this.args.newsletterContact != null,
        );
      }
    }
  }

  @action
  async save() {
    this.saving = true;
    this.error = undefined;
    try {
      await this.changeset.validate();
      if (this.changeset.get('isValid')) {
        this.showValidationErrors = false;
        this.changeset.changes.forEach(
          (change: { key: string; value: string }) => {
            if (isPresent(change.value) && change.value.trim) {
              this.changeset.set(change.key, change.value.trim());
            }
          },
        );
        await this.changeset.save();

        if (this.showSubscribeToNewsletterCheckbox) {
          let subscribeToNewsletter =
            this.changeset.get('subscribeToNewsletter') || false;
          if (subscribeToNewsletter && this.args.newsletterContact == null) {
            let newsletterContact = this.store.createRecord(
              'newsletter-contact',
              {
                contactId: this.args.contact.id,
              },
            );
            await newsletterContact.save();
            this.args.onNewsletterContactCreated(newsletterContact);
          } else if (
            !subscribeToNewsletter &&
            this.args.newsletterContact != null
          ) {
            await this.args.newsletterContact!.destroyRecord();
            this.args.onNewsletterContactDestroyed(
              this.args.newsletterContact!,
            );
          }
        }

        this.editing = false;
      } else {
        this.showValidationErrors = true;
      }
    } catch (error) {
      this.error = error;
    }
    this.saving = false;
  }
}
